import React, { useState } from 'react';
import { 
    Sun,
    Moon,
    Home,
    Building2,
    Navigation,
    Users,
    Lock,
    Eye,
    Grid,
    Calendar,
    DollarSign,
    XCircle,
    ZoomIn,
    ZoomOut,
    Maximize
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import VirtualModelViewer from '../../components/3d/VirtualModelViewer';
import { useVirtualModel } from '../../hooks/useVirtualModel';
import { useNavigation } from '../../hooks/useNavigation';
import { formatCurrency } from '../../utils/calculations';

const VirtualModelPage = () => {
  const { units, getStatusColor, getUnitsByFloor, handleUnitClick, getUnitById } = useVirtualModel();
  const { navigateTo } = useNavigation();
  
  const [activeTab, setActiveTab] = useState('exterior');
  const [floorSelected, setFloorSelected] = useState(10);
  const [viewMode, setViewMode] = useState('day');
  const [showFurniture, setShowFurniture] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showInfoPanel, setShowInfoPanel] = useState(true);
  
  const floors = [4, 5, 6, 7, 8, 9, 10];
  
  const handleZoomIn = () => {
    if (zoomLevel < 1.5) setZoomLevel(zoomLevel + 0.1);
  };
  
  const handleZoomOut = () => {
    if (zoomLevel > 0.7) setZoomLevel(zoomLevel - 0.1);
  };
  
  const handleUnitSelect = (unitId) => {
    const unit = getUnitById(unitId);
    setSelectedUnit(unit);
  };
  
  const handleStartSale = () => {
    if (selectedUnit && selectedUnit.status === 'available') {
      navigateTo('sales-flow', { unitId: selectedUnit.id });
    }
  };
  
  const handleReserveUnit = () => {
    if (selectedUnit && selectedUnit.status === 'available') {
      // Implementar lógica de reserva
      alert(`Unidade ${selectedUnit.number} reservada com sucesso!`);
    }
  };
  
  return (
    <MainLayout>
      <div className="flex flex-col bg-gray-100 rounded-lg overflow-hidden shadow-lg">
        {/* Header */}
        <div className="bg-white p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-blue-900">Resort Surfland Brasil - Maquete Virtual</h2>
          <div className="flex space-x-4">
            <button 
              className="px-3 py-1 bg-blue-900 text-white rounded-md text-sm flex items-center"
              onClick={handleReserveUnit}
              disabled={!selectedUnit || selectedUnit.status !== 'available'}
            >
              <Lock className="w-4 h-4 mr-1" /> Reservar Unidade
            </button>
            <button className="px-3 py-1 border border-blue-900 text-blue-900 rounded-md text-sm flex items-center">
              <Eye className="w-4 h-4 mr-1" /> Tour Guiado
            </button>
          </div>
        </div>
        
        {/* Toolbar */}
        <div className="bg-gray-50 p-2 border-b flex justify-between items-center">
          <div className="flex space-x-4">
            <button 
              className={`px-3 py-1 rounded-md text-sm flex items-center ${activeTab === 'exterior' ? 'bg-blue-900 text-white' : 'text-gray-700'}`}
              onClick={() => setActiveTab('exterior')}
            >
              <Building2 className="w-4 h-4 mr-1" /> Exterior
            </button>
            <button 
              className={`px-3 py-1 rounded-md text-sm flex items-center ${activeTab === 'interior' ? 'bg-blue-900 text-white' : 'text-gray-700'}`}
              onClick={() => setActiveTab('interior')}
            >
              <Home className="w-4 h-4 mr-1" /> Interior
            </button>
            <button 
              className={`px-3 py-1 rounded-md text-sm flex items-center ${activeTab === 'common' ? 'bg-blue-900 text-white' : 'text-gray-700'}`}
              onClick={() => setActiveTab('common')}
            >
              <Users className="w-4 h-4 mr-1" /> Áreas Comuns
            </button>
          </div>
          
          <div className="flex space-x-4">
            <button 
              className="p-1 rounded-full border"
              onClick={() => setViewMode(viewMode === 'day' ? 'night' : 'day')}
            >
              {viewMode === 'day' ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-blue-900" />}
            </button>
            <button 
              className={`p-1 rounded-full border ${showFurniture ? 'bg-blue-50 border-blue-900' : 'border-gray-300'}`}
              onClick={() => setShowFurniture(!showFurniture)}
            >
              <Grid className="w-5 h-5 text-blue-900" />
            </button>
            
            <div className="flex items-center space-x-1">
              <button 
                className="p-1 rounded-full border"
                onClick={handleZoomOut}
              >
                <ZoomOut className="w-5 h-5 text-blue-900" />
              </button>
              <span className="text-xs text-gray-500">{Math.round(zoomLevel * 100)}%</span>
              <button 
                className="p-1 rounded-full border"
                onClick={handleZoomIn}
              >
                <ZoomIn className="w-5 h-5 text-blue-900" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex flex-1 min-h-96">
          {/* Floor Selector */}
          <div className="w-16 bg-white border-r p-2 flex flex-col items-center">
            <span className="text-xs text-gray-500 mb-2">Andar</span>
            {floors.map(floor => (
              <button 
                key={floor}
                className={`w-10 h-10 mb-2 rounded-md flex items-center justify-center ${floor === floorSelected ? 'bg-blue-900 text-white' : 'bg-gray-100 text-gray-700'}`}
                onClick={() => setFloorSelected(floor)}
              >
                {floor}
              </button>
            ))}
          </div>
          
          {/* 3D Model View */}
          <div className="flex-1 relative bg-gray-200 overflow-hidden">
            <VirtualModelViewer
              onUnitSelect={handleUnitSelect}
              activeFloor={floorSelected}
              viewMode={viewMode}
              showFurniture={showFurniture}
            />
            
            {/* Overlays */}
            <div className="absolute top-2 left-2 bg-white bg-opacity-80 rounded p-2 text-xs">
              <div className="flex space-x-2">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-1"></div>
                  <span>Disponível</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-1"></div>
                  <span>Reservado</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
                  <span>Vendido</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Info Panel */}
          {showInfoPanel && (
            <div className="w-64 bg-white border-l overflow-y-auto">
              <div className="p-3 border-b flex justify-between items-center">
                <h3 className="font-bold text-gray-700">Detalhes da Unidade</h3>
                <button 
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => setShowInfoPanel(false)}
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
              
              {selectedUnit ? (
                <div className="p-3">
                  <div className="bg-gray-100 h-32 rounded flex items-center justify-center mb-3">
                    <span className="text-gray-400 text-sm">Vista da Unidade</span>
                  </div>
                  
                  <h4 className="font-bold text-blue-900">Unidade {selectedUnit.number}</h4>
                  <p className="text-sm text-gray-600 mb-2">Andar {selectedUnit.floor}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Tipo:</span>
                      <span className="font-medium">{selectedUnit.type}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Área:</span>
                      <span className="font-medium">{selectedUnit.area}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Status:</span>
                      <span className={`font-medium ${
                        selectedUnit.status === 'available' ? 'text-green-600' :
                        selectedUnit.status === 'reserved' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {selectedUnit.status === 'available' ? 'Disponível' :
                         selectedUnit.status === 'reserved' ? 'Reservado' : 'Vendido'}
                      </span>
                    </div>
                    {selectedUnit.status === 'available' && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Semanas disponíveis:</span>
                          <span className="font-medium">{selectedUnit.weeks}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Valor fração:</span>
                          <span className="font-medium text-blue-900">{formatCurrency(selectedUnit.price)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Valor apartamento:</span>
                          <span className="font-medium text-blue-900">{formatCurrency(selectedUnit.weekPrice)}</span>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {selectedUnit.status === 'available' && (
                    <div className="space-y-2">
                      <button 
                        className="w-full py-2 bg-blue-900 text-white rounded-md text-sm flex items-center justify-center"
                        onClick={() => navigateTo('unit-calendar', { unitId: selectedUnit.id })}
                      >
                        <Calendar className="w-4 h-4 mr-1" /> 
                        Ver Disponibilidade
                      </button>
                      <button 
                        className="w-full py-2 bg-green-600 text-white rounded-md text-sm flex items-center justify-center"
                        onClick={handleStartSale}
                      >
                        <DollarSign className="w-4 h-4 mr-1" /> 
                        Iniciar Venda
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-3 flex flex-col items-center justify-center h-64 text-center">
                  <Building2 className="w-16 h-16 text-gray-300 mb-4" />
                  <p className="text-gray-500 text-sm">Selecione uma unidade no modelo para ver seus detalhes</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 p-2 border-t flex justify-between items-center text-xs text-gray-500">
          <span>Torre Sul - Andar {floorSelected}</span>
          <span>{viewMode === 'day' ? 'Visualização Diurna' : 'Visualização Noturna'}</span>
        </div>
      </div>
    </MainLayout>
  );
};

export default VirtualModelPage;